import { useInfiniteQuery } from '@tanstack/react-query';

import { Groups, type PagingParams } from '@app/client';

import { getNextPageParam, groupsCacheKeys } from './utils';

type GetGroupMembershipParams = {
    primaryYat?: string;
    twitterHandle?: string;
    isAuthenticated?: boolean;
    groupId: string;
    paging?: PagingParams;
};

/**
 * React Hook using React Query to call `/groups/:groupId/members`.
 */
export const useGetGroupMembers = ({
    isAuthenticated = true,
    primaryYat,
    twitterHandle,
    groupId,
    paging,
}: GetGroupMembershipParams) => {
    const cache = [...groupsCacheKeys.members(groupId)];

    if (primaryYat) {
        cache.push(primaryYat);
    }
    if (twitterHandle) {
        cache.push(twitterHandle);
    }

    const filters = {
        ...(primaryYat ? { primary_yat: primaryYat } : {}),
        ...(twitterHandle ? { twitter_handle: twitterHandle } : {}),
    };

    return useInfiniteQuery(
        cache,
        ({ signal, pageParam = 0 }) =>
            Groups.callGetGroupMembers(
                {
                    groupId,
                    settings: { signal },
                    query: {
                        ...filters,
                        page: pageParam || 0,
                        limit: paging?.limit || 50,
                    },
                },
                isAuthenticated,
            ),
        {
            retry: false,
            enabled: Boolean(groupId),
            keepPreviousData: true,
            getNextPageParam,
        },
    );
};

export const useGetGroupFollowers = ({
    isAuthenticated = true,
    primaryYat,
    twitterHandle,
    groupId,
}: GetGroupMembershipParams) => {
    const cache = [...groupsCacheKeys.followers(groupId)];

    if (primaryYat) {
        cache.push(primaryYat);
    }
    if (twitterHandle) {
        cache.push(twitterHandle);
    }

    const filters = {
        ...(primaryYat ? { primary_yat: primaryYat } : {}),
        ...(twitterHandle ? { twitter_handle: twitterHandle } : {}),
    };

    return useInfiniteQuery(
        cache,
        ({ signal, pageParam = 0 }) =>
            Groups.callGetGroupFollowers(
                {
                    groupId,
                    settings: { signal },
                    query: {
                        ...filters,
                        page: pageParam || 0,
                        limit: 50,
                    },
                },
                isAuthenticated,
            ),
        {
            retry: false,
            enabled: Boolean(groupId),
            keepPreviousData: true,
            getNextPageParam,
        },
    );
};
